import { Helmet } from "react-helmet-async";
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";
import UrduKeyboard from "../Components/UrduKeyboard";
import { useBackspaceNavigation } from "../hooks/useBackspaceNavigation";

const normalizeText = (value) =>
  (value || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

const normalizeVehicle = (value) =>
  (value || "").toString().trim().toLowerCase().replace(/\s+/g, "").replace(/-/g, "");

const unitToUrdu = (unitRaw) => {
  const u = (unitRaw || "").toString().trim().toLowerCase();
  if (!u) return "";
  // Common unit translations (you can extend this list anytime)
  const map = {
    crate: "کریٹس",
    crates: "کریٹس",
    bag: "بوریاں",
    bags: "بوریاں",
    box: "ڈبے",
    boxes: "ڈبے",
    kg: "کلو",
    kgs: "کلو",
    kilogram: "کلو",
    kilograms: "کلو",
    ton: "ٹن",
    tons: "ٹن",
  };
  return map[u] || unitRaw; // if unknown, keep original
};

function CreateSale() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const editSaleId = searchParams.get('edit');

  // Backspace navigation to dashboard
  useBackspaceNavigation();

  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [language, setLanguage] = useState('ur'); // Default to Urdu
  const [companyLanguage, setCompanyLanguage] = useState('ur');

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };

  const [formData, setFormData] = useState({
    serialNumber: '',
    customerPartyId: '',
    supplierName: '',
    supplierNameUrdu: '',
    supplierVehicleNumber: '',
    sellerName: '',
    sellerNameUrdu: '',
    sellerNumber: '',
    date: new Date().toLocaleDateString('en-CA'),
    items: [
      { description: '', descriptionUrdu: '', unit: '', dana: '', quantity: '', ratePerUnit: '', lineTotal: 0 }
    ],
    subtotal: 0,
    discount: 0,
    totalAmount: 0,
    paymentMethodId: '',
    paymentMethod: '', // keep name for receipts/backward-compat
    amountPaid: ''
  });

  const [focusedFields, setFocusedFields] = useState({});
  const [ledgerParties, setLedgerParties] = useState([]); // گاہک کھاتہ (customers)
  const [supplierParties, setSupplierParties] = useState([]); // سپلائر کھاتہ (suppliers)
  const [vehicleNumbers, setVehicleNumbers] = useState([]); // Vehicle numbers from bills
  const [selectedSales, setSelectedSales] = useState([]);
  const [showFilterSearchUrduKeyboard, setShowFilterSearchUrduKeyboard] = useState(false);
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCustomerUrduKeyboard, setShowCustomerUrduKeyboard] = useState(false);
  const customerDropdownRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showSupplierUrduKeyboard, setShowSupplierUrduKeyboard] = useState(false);
  const supplierDropdownRef = useRef(null);
  const supplierSearchInputRef = useRef(null);
  const [bills, setBills] = useState([]); // Store bills to find supplier by vehicle number
  const [allSales, setAllSales] = useState([]); // All sale receipts (for remaining stock calculation)
  const [originalSale, setOriginalSale] = useState(null); // For edit mode: exclude current sale from sold qty
  const [paymentMethods, setPaymentMethods] = useState([]); // Payment methods from admin
  const [availableItemNames, setAvailableItemNames] = useState([]); // Item names from bills for selected supplier
  const [availableUnits, setAvailableUnits] = useState([]); // Units from bills (for suggestions)
  const [itemSuggestions, setItemSuggestions] = useState({}); // Suggestions for each item row

  // Refs for fields to handle Enter key navigation
  const fieldRefs = useRef({});

  useEffect(() => {
    fetchCompanyData();
    if (editSaleId) {
      fetchSaleData(editSaleId);
    }
  }, [editSaleId]);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
        setShowSupplierDropdown(false);
      }
    };

    if (showCustomerDropdown || showSupplierDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown, showSupplierDropdown]);

  // Allow forcing language via URL (?lang=en or ?lang=ur)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const forcedLang = params.get('lang');
    if (forcedLang === 'en' || forcedLang === 'ur') {
      setLanguage(forcedLang);
      setCompanyLanguage(forcedLang);
    }
  }, [location.search]);



  useEffect(() => {
    // Calculate totals whenever items or discount change
    calculateTotals();
  }, [formData.items, formData.discount]);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        toast.error("براہ کرم لاگ ان کریں");
        navigate("/company/login");
        return;
      }

      const [profileResponse, salesResponse] = await Promise.all([
        api.get("/company/profile", { headers: { Authorization: `Bearer ${token}` } }),
        api.get("/sale/company/all", { headers: { Authorization: `Bearer ${token}` } }),
        fetchLedgerParties(),
        fetchSupplierParties(),
        fetchPaymentMethods(),
        fetchVehicleNumbersFromBills()
      ]);

      const companyData = profileResponse.data.company;
      setCompany(companyData);
      const companyLang = companyData?.language || 'ur';
      setLanguage(companyLang);
      setCompanyLanguage(companyLang);

      setAllSales(salesResponse.data.sales || []);

      let nextSerial = 1;
      if (salesResponse.data.sales && salesResponse.data.sales.length > 0) {
        const validSerials = salesResponse.data.sales
          .map(sale => {
            const sn = sale.serialNumber?.toString().trim() || '';
            const num = parseInt(sn, 10);
            return !isNaN(num) && num > 0 ? num : null;
          })
          .filter(num => num !== null);

        if (validSerials.length > 0) {
          const maxSerial = Math.max(...validSerials);
          nextSerial = maxSerial + 1;
        } else {
          nextSerial = salesResponse.data.sales.length + 1;
        }
      }

      setFormData(prev => ({ ...prev, serialNumber: String(nextSerial) }));
    } catch (error) {
      console.error("Fetch company error:", error);
      toast.error("کمپنی ڈیٹا لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSaleData = async (saleId) => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get(`/sale/company/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sale = response.data.sale;
      setOriginalSale(sale);

      // Debug: Log sale items to check dana field
      console.log('Sale items from API:', sale.items);
      if (sale.items && sale.items.length > 0) {
        sale.items.forEach((item, idx) => {
          console.log(`Item ${idx} dana:`, item.dana, typeof item.dana);
        });
      }

      // Populate form with sale data
      setFormData({
        serialNumber: sale.serialNumber || '',
        customerPartyId: sale.customerPartyId || '',
        supplierName: sale.supplierName || '',
        supplierNameUrdu: sale.supplierNameUrdu || '',
        supplierVehicleNumber: sale.supplierVehicleNumber || '',
        sellerName: sale.sellerName || '',
        sellerNameUrdu: sale.sellerNameUrdu || '',
        sellerNumber: sale.sellerNumber || '',
        date: sale.date ? new Date(sale.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
        items: sale.items && sale.items.length > 0 ? sale.items.map(item => ({
          description: item.description || '',
          descriptionUrdu: item.descriptionUrdu || '',
          unit: item.unit || '',
          dana: item.dana !== null && item.dana !== undefined ? (typeof item.dana === 'number' ? item.dana : parseFloat(item.dana) || '') : '',
          quantity: item.quantity || '',
          ratePerUnit: item.ratePerUnit || '',
          lineTotal: item.lineTotal || 0
        })) : [{ description: '', descriptionUrdu: '', unit: '', dana: '', quantity: '', ratePerUnit: '', lineTotal: 0 }],
        subtotal: sale.subtotal || 0,
        discount: sale.discount || 0,
        totalAmount: sale.totalAmount || 0,
        paymentMethodId: sale.paymentMethodId || '',
        paymentMethod: sale.paymentMethod || '',
        amountPaid: sale.amountPaid || ''
      });

      // Clear supplier search term when editing (form will show supplier name from formData)
      setSupplierSearchTerm('');
    } catch (error) {
      console.error("Fetch sale error:", error);
      toast.error("سیل ڈیٹا لوڈ کرنے میں ناکام");
    }
  };

  // Build stock maps for quick lookup: key = supplier||vehicle||item
  const stockQtyMap = useMemo(() => {
    const map = new Map();
    (bills || []).forEach((bill) => {
      const supplier = normalizeText(bill?.customerNameUrdu || bill?.customerName);
      const vehicle = normalizeVehicle(bill?.expenses?.ActualVehicle || bill?.expenses?.Vehicle);
      const item = normalizeText(bill?.expenses?.ItemName);
      if (!vehicle || !item) return;
      const qty = parseFloat(bill?.expenses?.ItemQuantity) || 0;
      const key = `${supplier}||${vehicle}||${item}`;
      map.set(key, (map.get(key) || 0) + qty);
    });
    return map;
  }, [bills]);

  const soldQtyMap = useMemo(() => {
    const map = new Map();

    // Helper to add items from a sale
    const addSale = (sale, sign = 1) => {
      const supplier = normalizeText(sale?.supplierNameUrdu || sale?.supplierName);
      const vehicle = normalizeVehicle(sale?.supplierVehicleNumber);
      if (!vehicle) return;
      const items = Array.isArray(sale?.items) ? sale.items : [];
      items.forEach((it) => {
        const item = normalizeText(it?.descriptionUrdu || it?.description);
        if (!item) return;
        const qty = (parseFloat(it?.quantity) || 0) * sign;
        const key = `${supplier}||${vehicle}||${item}`;
        map.set(key, (map.get(key) || 0) + qty);
      });
    };

    (allSales || []).forEach((sale) => addSale(sale, 1));

    // In edit mode, exclude current sale from sold qty so remaining isn't reduced twice
    if (originalSale?._id) {
      addSale(originalSale, -1);
    }

    return map;
  }, [allSales, originalSale]);

  const getRemainingQtyForItem = (itemName, excludeIndex = null) => {
    const supplier = normalizeText(formData.supplierNameUrdu || formData.supplierName);
    const vehicle = normalizeVehicle(formData.supplierVehicleNumber);
    const item = normalizeText(itemName);
    if (!vehicle || !item) return null;

    const key = `${supplier}||${vehicle}||${item}`;
    const stockQty = stockQtyMap.get(key) || 0;
    const soldQty = soldQtyMap.get(key) || 0;
    let remaining = stockQty - soldQty;

    // subtract quantities already entered in this form for same item (other rows)
    if (excludeIndex !== null) {
      const inForm = (formData.items || []).reduce((sum, it, idx) => {
        if (idx === excludeIndex) return sum;
        const itName = it?.descriptionUrdu || it?.description || "";
        if (normalizeText(itName) !== item) return sum;
        return sum + (parseFloat(it?.quantity) || 0);
      }, 0);
      remaining -= inForm;
    }

    return remaining < 0 ? 0 : remaining;
  };

  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/ledger/parties?type=customer", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
    } catch (error) {
      console.error("Fetch ledger parties error:", error);
    }
  };

  const fetchSupplierParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/ledger/parties?type=supplier", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplierParties(response.data.parties || []);
    } catch (error) {
      console.error("Fetch supplier parties error:", error);
    }
  };

  const fetchVehicleNumbersFromBills = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Fetch recent bills to get vehicle numbers (limit to 1000 for performance)
      const billsResponse = await api.get("/bill/company/my-bills?limit=1000", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const billsData = billsResponse.data.bills || [];
      setBills(billsData); // Store bills for later use

      // Extract unique vehicle numbers from bills
      const vehicleNumberSet = new Set();

      billsData.forEach(bill => {
        const vehicleNumber = bill.expenses?.ActualVehicle || '';
        if (vehicleNumber && vehicleNumber.trim() !== '') {
          vehicleNumberSet.add(vehicleNumber.trim());
        }
      });

      // Convert to sorted array
      const vehicleNumbersList = Array.from(vehicleNumberSet).sort();
      setVehicleNumbers(vehicleNumbersList);
    } catch (error) {
      console.error("Fetch vehicle numbers from bills error:", error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/payment-method/active");
      setPaymentMethods(response.data.paymentMethods || []);
    } catch (error) {
      console.error("Fetch payment methods error:", error);
      // Fallback to default methods if API fails
      setPaymentMethods([
        { name: 'نقد رقم', nameUrdu: 'نقد رقم' },
        { name: 'Easypaisa', nameUrdu: 'ایزی پیسہ' },
        { name: 'Punjab Bank', nameUrdu: 'پنجاب بینک' }
      ]);
    }
  };

  // In edit mode: if paymentMethodId missing but paymentMethod string exists, try to map it
  useEffect(() => {
    if (!editSaleId) return;
    if (formData.paymentMethodId) return;
    if (!formData.paymentMethod) return;
    if (!paymentMethods || paymentMethods.length === 0) return;

    const pm = paymentMethods.find((m) => {
      const v = (formData.paymentMethod || '').toString().trim();
      return (m.nameUrdu || '').toString().trim() === v || (m.name || '').toString().trim() === v;
    });
    if (pm?._id) {
      setFormData((prev) => ({ ...prev, paymentMethodId: pm._id }));
    }
  }, [editSaleId, formData.paymentMethodId, formData.paymentMethod, paymentMethods]);

  const handleVehicleNumberChange = (vehicleNumber) => {
    handleInputChange('supplierVehicleNumber', vehicleNumber);

    // Find bill with this vehicle number and auto-fill supplier name and details
    if (vehicleNumber && vehicleNumber.trim() !== '' && bills.length > 0) {
      const selectedVehicleNorm = normalizeVehicle(vehicleNumber);
      // Find all bills with this vehicle number
      const matchingBills = bills.filter(bill => {
        const billVehicleNumber = bill.expenses?.ActualVehicle || bill.expenses?.Vehicle || '';
        return normalizeVehicle(billVehicleNumber) === selectedVehicleNorm;
      });

      if (matchingBills.length > 0) {
        // Sort by date (most recent first)
        matchingBills.sort((a, b) => {
          const dateA = new Date(a.date || a.createdAt || 0);
          const dateB = new Date(b.date || b.createdAt || 0);
          return dateB - dateA;
        });

        const latestBill = matchingBills[0];
        const supplierName = latestBill.customerName || '';
        const supplierNameUrdu = latestBill.customerNameUrdu || '';

        // Try to find matching supplier in supplierParties
        let matchingSupplierParty = null;
        if (supplierParties.length > 0 && (supplierName || supplierNameUrdu)) {
          const supplierNameNorm = normalizeText(supplierNameUrdu || supplierName);
          matchingSupplierParty = supplierParties.find(party => {
            const partyNameNorm = normalizeText(party.nameUrdu || party.name);
            return partyNameNorm === supplierNameNorm;
          });
        }

        // If supplier party found, use handleSupplierSelect to set all details
        if (matchingSupplierParty) {
          handleSupplierSelect(matchingSupplierParty._id);
          setSupplierSearchTerm(''); // Clear search term
        } else {
          // If supplier not found in parties, just set the name manually
          setFormData(prev => ({
            ...prev,
            supplierName: supplierName,
            supplierNameUrdu: supplierNameUrdu,
            supplierVehicleNumber: vehicleNumber
          }));
          setSupplierSearchTerm(''); // Clear search term

          // Update available items and units for this vehicle
          const vehicleNorm = normalizeVehicle(vehicleNumber);
          const supplierBills = bills.filter(bill => {
            const billVehicle = bill.expenses?.ActualVehicle || bill.expenses?.Vehicle || '';
            return normalizeVehicle(billVehicle) === vehicleNorm;
          });

          const itemNamesSet = new Set();
          const unitsSet = new Set();
          supplierBills.forEach(bill => {
            const itemName = bill.expenses?.ItemName;
            const unit = bill.expenses?.ItemUnit;
            if (itemName) itemNamesSet.add(itemName);
            if (unit) unitsSet.add(unit);
          });

          setAvailableItemNames(Array.from(itemNamesSet));
          setAvailableUnits(Array.from(unitsSet));
        }

        // Auto-fill item name from latest bill to first item
        const itemNameFromBill = latestBill.expenses?.ItemName || '';
        if (itemNameFromBill && itemNameFromBill.trim() !== '') {
          // Unit should be fetched by matching BOTH vehicle + item (not just latest bill)
          const unitForItem = getLatestUnitFromBills(vehicleNumber, itemNameFromBill.trim());
          const updatedItems = [...formData.items];
          if (updatedItems.length > 0) {
            updatedItems[0] = {
              ...updatedItems[0],
              descriptionUrdu: itemNameFromBill.trim(),
              description: itemNameFromBill.trim(),
              unit: updatedItems[0].unit || (companyLanguage === 'ur' ? unitToUrdu(unitForItem) : unitForItem) || ''
            };
            setFormData(prev => ({ ...prev, items: updatedItems }));
          }
        }

        // Extract unique item names from all matching bills
        const itemNameSet = new Set();
        const unitSet = new Set();
        matchingBills.forEach(bill => {
          const itemName = bill.expenses?.ItemName || '';
          if (itemName && itemName.trim() !== '') {
            itemNameSet.add(itemName.trim());
          }
          const u = (bill.expenses?.ItemUnit || bill.expenses?.itemUnit || '').toString().trim();
          if (u) unitSet.add(companyLanguage === 'ur' ? unitToUrdu(u) : u);
        });

        // Convert to sorted array
        const itemNamesList = Array.from(itemNameSet).sort();
        setAvailableItemNames(itemNamesList);
        setAvailableUnits(Array.from(unitSet).sort());
      } else {
        setAvailableItemNames([]);
        setAvailableUnits([]);
      }
    } else {
      setAvailableItemNames([]);
      setAvailableUnits([]);
    }
  };

  const getLatestUnitFromBills = (vehicleNumber, itemName) => {
    if (!vehicleNumber || !itemName || !bills?.length) return '';
    const v = normalizeVehicle(vehicleNumber);
    const it = normalizeText(itemName);
    const candidates = bills.filter((bill) => {
      const bv = normalizeVehicle((bill?.expenses?.ActualVehicle || bill?.expenses?.Vehicle || '').toString());
      const bi = normalizeText((bill?.expenses?.ItemName || '').toString());
      return bv === v && bi === it;
    });
    if (!candidates.length) return '';
    candidates.sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));
    return (candidates[0]?.expenses?.ItemUnit || candidates[0]?.expenses?.itemUnit || '').toString().trim();
  };

  // Handle Urdu keyboard input for customer search
  const handleCustomerSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setCustomerSearchTerm(prev => prev ? prev.slice(0, -1) : '');
    } else {
      setCustomerSearchTerm(prev => (prev || '') + key);
      setShowCustomerDropdown(true);
    }
  };

  // Toggle Urdu keyboard on customer search input click
  const handleCustomerSearchInputClick = () => {
    setShowCustomerUrduKeyboard(prev => !prev);
  };

  const handleCustomerSelect = (partyId) => {
    const party = ledgerParties.find(p => p._id === partyId);
    if (party) {
      setFormData(prev => ({
        ...prev,
        customerPartyId: partyId,
        sellerName: party.name,
        sellerNameUrdu: party.nameUrdu || party.name,
        sellerNumber: party.phone || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customerPartyId: '',
        sellerName: '',
        sellerNameUrdu: '',
        sellerNumber: ''
      }));
    }
  };

  const handleSupplierSelect = (partyId) => {
    const party = supplierParties.find(p => p._id === partyId);
    if (party) {
      const supplierName = party.name;
      const supplierNameUrdu = party.nameUrdu || party.name;

      // Find vehicle number from bills for this supplier
      let vehicleNumber = '';
      if (bills.length > 0) {
        const supplierNameNorm = normalizeText(supplierNameUrdu || supplierName);
        // Find the most recent bill with this supplier
        const matchingBills = bills.filter(bill => {
          const billSupplierName = bill.customerNameUrdu || bill.customerName || '';
          return normalizeText(billSupplierName) === supplierNameNorm;
        });

        if (matchingBills.length > 0) {
          // Get vehicle number from the most recent bill
          const mostRecentBill = matchingBills.sort((a, b) =>
            new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt)
          )[0];
          vehicleNumber = mostRecentBill.expenses?.ActualVehicle || mostRecentBill.expenses?.Vehicle || '';
        }
      }

      setFormData(prev => ({
        ...prev,
        supplierName: supplierName,
        supplierNameUrdu: supplierNameUrdu,
        supplierVehicleNumber: vehicleNumber
      }));

      // Update available items and units for this supplier
      if (vehicleNumber && bills.length > 0) {
        const supplierNameNorm = normalizeText(supplierNameUrdu || supplierName);
        const vehicleNorm = normalizeVehicle(vehicleNumber);
        const supplierBills = bills.filter(bill => {
          const billSupplierName = bill.customerNameUrdu || bill.customerName || '';
          const billVehicle = bill.expenses?.ActualVehicle || bill.expenses?.Vehicle || '';
          return normalizeText(billSupplierName) === supplierNameNorm &&
            normalizeVehicle(billVehicle) === vehicleNorm;
        });

        const itemNamesSet = new Set();
        const unitsSet = new Set();
        supplierBills.forEach(bill => {
          const itemName = bill.expenses?.ItemName;
          const unit = bill.expenses?.ItemUnit;
          if (itemName) itemNamesSet.add(itemName);
          if (unit) unitsSet.add(unit);
        });

        setAvailableItemNames(Array.from(itemNamesSet));
        setAvailableUnits(Array.from(unitsSet));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        supplierName: '',
        supplierNameUrdu: '',
        supplierVehicleNumber: ''
      }));
      setAvailableItemNames([]);
      setAvailableUnits([]);
    }
  };

  // Handle Urdu keyboard input for supplier search
  const handleSupplierSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSupplierSearchTerm(prev => prev ? prev.slice(0, -1) : '');
    } else {
      setSupplierSearchTerm(prev => (prev || '') + key);
      setShowSupplierDropdown(true);
    }
  };

  // Toggle Urdu keyboard on supplier search input click
  const handleSupplierSearchInputClick = () => {
    setShowSupplierUrduKeyboard(prev => !prev);
  };

  const calculateTotals = () => {
    let subtotal = 0;
    formData.items.forEach(item => {
      const qty = parseFloat(item.quantity) || 0;
      const rate = parseFloat(item.ratePerUnit) || 0;
      const lineTotal = qty * rate;
      subtotal += lineTotal;
    });

    const discount = parseFloat(formData.discount) || 0;
    const totalAmount = subtotal - discount;

    setFormData(prev => ({
      ...prev,
      subtotal: subtotal,
      totalAmount: totalAmount
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    // Ensure all fields are preserved, especially dana
    const currentItem = updatedItems[index] || {};
    updatedItems[index] = {
      description: currentItem.description || '',
      descriptionUrdu: currentItem.descriptionUrdu || '',
      unit: currentItem.unit || '',
      dana: currentItem.dana !== undefined ? currentItem.dana : '',
      quantity: currentItem.quantity || '',
      ratePerUnit: currentItem.ratePerUnit || '',
      lineTotal: currentItem.lineTotal || 0,
      [field]: value
    };

    // Debug log for dana field
    if (field === 'dana') {
      console.log('handleItemChange - dana update:', {
        index,
        field,
        value,
        currentItem: currentItem,
        updatedItem: updatedItems[index],
        allItems: updatedItems
      });
    }

    // Calculate line total
    if (field === 'quantity' || field === 'ratePerUnit') {
      // Enforce max quantity based on remaining stock
      if (field === 'quantity') {
        const itemName = updatedItems[index]?.descriptionUrdu || updatedItems[index]?.description || '';
        const remaining = getRemainingQtyForItem(itemName, index);
        if (remaining !== null) {
          const attempted = parseFloat(value) || 0;
          if (attempted > remaining) {
            updatedItems[index].quantity = remaining === 0 ? 0 : remaining;
          }
        }
      }

      const qty = parseFloat(updatedItems[index].quantity) || 0;
      const rate = parseFloat(updatedItems[index].ratePerUnit) || 0;
      updatedItems[index].lineTotal = qty * rate;
    }

    // Filter item suggestions based on input
    if (field === 'descriptionUrdu' || field === 'description') {
      const searchValue = value.toLowerCase().trim();
      if (searchValue && availableItemNames.length > 0) {
        const filtered = availableItemNames.filter(itemName =>
          itemName.toLowerCase().includes(searchValue)
        );
        setItemSuggestions(prev => ({ ...prev, [index]: filtered }));
      } else {
        setItemSuggestions(prev => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }
    }

    setFormData(prev => {
      const newState = { ...prev, items: updatedItems };
      // Debug: Log formData after update
      if (field === 'dana') {
        console.log('FormData after dana update:', newState.items[index]);
        console.log('All items:', newState.items);
      }
      return newState;
    });
  };

  const handleItemNameSelect = (index, itemName) => {
    handleItemChange(index, 'descriptionUrdu', itemName);
    handleItemChange(index, 'description', itemName);

    // Auto-fill unit from latest matching bill (vehicle + item)
    const unitFromBill = getLatestUnitFromBills(formData.supplierVehicleNumber, itemName);
    if (unitFromBill) {
      handleItemChange(index, 'unit', companyLanguage === 'ur' ? unitToUrdu(unitFromBill) : unitFromBill);
    }

    setItemSuggestions(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  // Handle Enter key to move to next field
  const handleKeyDown = (e, currentField, itemIndex = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      let nextField = null;

      if (itemIndex !== null) {
        // For item fields
        const itemFields = ['description', 'unit', 'dana', 'quantity', 'ratePerUnit'];
        const currentItemFieldIndex = itemFields.indexOf(currentField);

        if (currentItemFieldIndex < itemFields.length - 1) {
          // Move to next field in same item row
          nextField = `item_${itemIndex}_${itemFields[currentItemFieldIndex + 1]}`;
        } else if (currentItemFieldIndex === itemFields.length - 1) {
          // Move to first field of next item, or to discount if last item
          if (itemIndex < formData.items.length - 1) {
            nextField = `item_${itemIndex + 1}_description`;
          } else {
            nextField = 'discount';
          }
        }
      } else {
        // For main form fields
        switch (currentField) {
          case 'date':
            nextField = 'customerPartyId';
            break;
          case 'customerPartyId':
            nextField = 'sellerNumber';
            break;
          case 'sellerNumber':
            nextField = 'supplierName';
            break;
          case 'supplierName':
            nextField = 'supplierVehicleNumber';
            break;
          case 'supplierVehicleNumber':
            // After supplier vehicle number, move to first item description
            nextField = 'item_0_description';
            break;
          case 'discount':
            nextField = 'paymentMethod';
            break;
          case 'paymentMethod':
            nextField = 'amountPaid';
            break;
          case 'amountPaid':
            // Last field - do nothing or could submit form
            return;
          default:
            return;
        }
      }

      // Focus on next field
      if (nextField && fieldRefs.current[nextField]) {
        setTimeout(() => {
          fieldRefs.current[nextField].focus();
          // For select fields, try to open dropdown (some browsers may not support this)
          if (fieldRefs.current[nextField].tagName === 'SELECT') {
            fieldRefs.current[nextField].focus();
          }
        }, 10);
      }
    }
  };

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', descriptionUrdu: '', unit: '', dana: '', quantity: '', ratePerUnit: '', lineTotal: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: updatedItems }));
    }
  };

  const handleFocus = (field, index = null) => {
    const key = index !== null ? `${field}_${index}` : field;
    setFocusedFields(prev => ({ ...prev, [key]: true }));

    // Clear 0 values
    if (index !== null) {
      const item = formData.items[index];
      if (item[field] === 0 || item[field] === '0') {
        handleItemChange(index, field, '');
      }
    } else {
      const value = formData[field];
      if (value === 0 || value === '0') {
        handleInputChange(field, '');
      }
    }
  };

  const handleBlur = (field, index = null) => {
    const key = index !== null ? `${field}_${index}` : field;
    setFocusedFields(prev => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });

    // Close item suggestions when blurring item name field
    if (index !== null && (field === 'descriptionUrdu' || field === 'description')) {
      // Delay closing to allow click on suggestion
      setTimeout(() => {
        setItemSuggestions(prev => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }, 200);
    }

    // Restore 0 if empty
    if (index !== null) {
      const item = formData.items[index];
      if (item[field] === '' || item[field] === null || item[field] === undefined) {
        if (field === 'quantity' || field === 'ratePerUnit') {
          handleItemChange(index, field, 0);
        } else {
          handleItemChange(index, field, '');
        }
      }
    } else {
      const value = formData[field];
      if (value === '' || value === null || value === undefined) {
        if (field === 'subtotal' || field === 'discount' || field === 'totalAmount' || field === 'amountPaid') {
          handleInputChange(field, 0);
        } else {
          handleInputChange(field, '');
        }
      }
    }
  };

  const getDisplayValue = (field, index = null) => {
    if (index !== null) {
      const item = formData.items[index];
      const value = item[field];
      const key = `${field}_${index}`;
      if (focusedFields[key] && (value === 0 || value === '0')) {
        return '';
      }
      return value || '';
    } else {
      const value = formData[field];
      if (focusedFields[field] && (value === 0 || value === '0')) {
        return '';
      }
      return value || '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const token = localStorage.getItem("companyToken");

      // Require customer selection (گاہک کھاتہ)
      if (!formData.customerPartyId) {
        toast.error(companyLanguage === 'ur' ? "گاہک کا نام لازمی ہے" : "Customer name is required");
        try {
          fieldRefs.current?.customerPartyId?.focus?.();
        } catch (err) {
          // ignore focus errors
        }
        setIsSaving(false);
        return;
      }

      // Require supplier name
      if (!formData.supplierName && !formData.supplierNameUrdu) {
        toast.error(companyLanguage === 'ur' ? "سپلائر کا نام لازمی ہے" : "Supplier name is required");
        try {
          fieldRefs.current?.supplierName?.focus?.();
        } catch (err) {
          // ignore focus errors
        }
        setIsSaving(false);
        return;
      }

      // Validate required item fields (dana, quantity, ratePerUnit)
      for (let i = 0; i < formData.items.length; i++) {
        const item = formData.items[i];
        if (!item.dana || item.dana === '' || item.dana === null || item.dana === undefined) {
          toast.error(companyLanguage === 'ur' ? `آئٹم ${i + 1} کا دانہ لازمی ہے` : `Item ${i + 1} dana is required`);
          try {
            fieldRefs.current?.[`item_${i}_dana`]?.focus?.();
          } catch (err) {
            // ignore focus errors
          }
          setIsSaving(false);
          return;
        }
        if (!item.quantity || item.quantity === '' || item.quantity === null || item.quantity === undefined || parseFloat(item.quantity) <= 0) {
          toast.error(companyLanguage === 'ur' ? `آئٹم ${i + 1} کی مقدار لازمی ہے` : `Item ${i + 1} quantity is required`);
          try {
            fieldRefs.current?.[`item_${i}_quantity`]?.focus?.();
          } catch (err) {
            // ignore focus errors
          }
          setIsSaving(false);
          return;
        }
        if (!item.ratePerUnit || item.ratePerUnit === '' || item.ratePerUnit === null || item.ratePerUnit === undefined || parseFloat(item.ratePerUnit) <= 0) {
          toast.error(companyLanguage === 'ur' ? `آئٹم ${i + 1} کی فی یونٹ قیمت لازمی ہے` : `Item ${i + 1} rate per unit is required`);
          try {
            fieldRefs.current?.[`item_${i}_ratePerUnit`]?.focus?.();
          } catch (err) {
            // ignore focus errors
          }
          setIsSaving(false);
          return;
        }
      }

      // Validate quantities against remaining stock (per item row)
      for (let i = 0; i < (formData.items || []).length; i++) {
        const it = formData.items[i];
        const itemName = it?.descriptionUrdu || it?.description || '';
        const remaining = getRemainingQtyForItem(itemName, i);
        if (remaining !== null) {
          const qty = parseFloat(it?.quantity) || 0;
          if (qty > remaining) {
            toast.error(companyLanguage === 'ur' ? "مقدار باقی اسٹاک سے زیادہ نہیں ہو سکتی" : "Quantity cannot exceed remaining stock");
            try {
              fieldRefs.current?.[`item_${i}_quantity`]?.focus?.();
            } catch (err) {
              // ignore focus errors
            }
            setIsSaving(false);
            return;
          }
        }
      }

      // Clean items data
      const cleanedItems = formData.items.map(item => {
        // Handle dana as numeric value
        let danaValue = null;
        if (item.dana !== null && item.dana !== undefined && item.dana !== '') {
          const parsed = parseFloat(item.dana);
          danaValue = !isNaN(parsed) ? parsed : null;
        }

        return {
          description: item.description || '',
          descriptionUrdu: item.descriptionUrdu || '',
          unit: item.unit || '',
          dana: danaValue,
          quantity: parseFloat(item.quantity) || 0,
          ratePerUnit: parseFloat(item.ratePerUnit) || 0,
          lineTotal: parseFloat(item.lineTotal) || 0
        };
      });

      const totalItems = cleanedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

      const selectedPM = paymentMethods.find((m) => (m._id?.toString() || m._id) === (formData.paymentMethodId?.toString() || formData.paymentMethodId));
      const paymentMethodName = selectedPM?.nameUrdu || selectedPM?.name || formData.paymentMethod || '';

      // Debug: Log cleanedItems to verify dana field
      console.log('Cleaned items before sending to backend:', cleanedItems);
      cleanedItems.forEach((item, idx) => {
        console.log(`Item ${idx} dana value:`, item.dana, typeof item.dana);
      });

      const salePayload = {
        serialNumber: formData.serialNumber,
        supplierName: formData.supplierName,
        supplierNameUrdu: formData.supplierNameUrdu,
        supplierVehicleNumber: formData.supplierVehicleNumber,
        sellerName: formData.sellerName,
        sellerNameUrdu: formData.sellerNameUrdu,
        sellerNumber: formData.sellerNumber,
        customerPartyId: formData.customerPartyId || null,
        items: cleanedItems,
        totalItems: totalItems,
        subtotal: parseFloat(formData.subtotal) || 0,
        discount: parseFloat(formData.discount) || 0,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        paymentMethodId: formData.paymentMethodId || null,
        paymentMethod: paymentMethodName || '',
        amountPaid: parseFloat(formData.amountPaid) || 0,
        date: formData.date || new Date().toLocaleDateString('en-CA')
      };

      // Debug: Log full payload
      console.log('Sale payload being sent to backend:', JSON.stringify(salePayload, null, 2));

      if (editSaleId) {
        // Update existing sale
        const response = await api.put(`/sale/company/${editSaleId}`, salePayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("سیل اپ ڈیٹ ہو گیا!");
      } else {
        // Create new sale
        const response = await api.post("/sale/company/create", salePayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("سیل بن گیا!");
      }
      navigate('/company/dashboard');
    } catch (error) {
      console.error("Create sale error:", error);
      toast.error(error.response?.data?.message || "سیل بنانے میں ناکام");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">لوڈ ہو رہا ہے...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Company Header - Fixed at Top */}
      {/* Company Header Hidden */}
      {/* {company && (
        <DashboardHeader 
          company={company} 
          companyLanguage={companyLanguage} 
          onLogout={handleLogout} 
        />
      )} */}

      <Helmet>
        <title>{editSaleId ? 'سیل تبدیل کریں' : 'نیا سیل بنائیں'}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-100 pt-[6px] pb-8 px-4" dir="rtl">
        <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg p-6">
          <h1 className="text-2xl font-bold text-center mb-6 text-gray-900">
            {editSaleId ? 'سیل تبدیل کریں' : 'سیل رسید'}
          </h1>

          <form onSubmit={handleSubmit}>
            {/* Top Section - Merchant and Supplier Info */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Right Side - Merchant Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 mb-3">گاہک کی معلومات</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رسید نمبر:</label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700 bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ:</label>
                  <input
                    type="date"
                    value={formData.date || new Date().toLocaleDateString('en-CA')}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'date')}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    ref={(el) => fieldRefs.current['date'] = el}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700 cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">گاہک کا نام (کھاتہ):</label>
                  <div className="relative" ref={customerDropdownRef}>
                    <input
                      type="text"
                      value={formData.customerPartyId ? (ledgerParties.find(p => p._id === formData.customerPartyId)?.nameUrdu || ledgerParties.find(p => p._id === formData.customerPartyId)?.name || '') : customerSearchTerm}
                      onChange={(e) => {
                        setCustomerSearchTerm(e.target.value);
                        setShowCustomerDropdown(true);
                        if (!e.target.value) {
                          handleCustomerSelect('');
                        }
                      }}
                      onClick={handleCustomerSearchInputClick}
                      onFocus={() => setShowCustomerDropdown(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !showCustomerDropdown) {
                          handleKeyDown(e, 'customerPartyId');
                        }
                      }}
                      ref={(el) => {
                        customerSearchInputRef.current = el;
                        fieldRefs.current['customerPartyId'] = el;
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700"
                      placeholder="گاہک تلاش کریں..."
                      style={{
                        fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                        lineHeight: '1.8',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        fontSize: '13px',
                        height: 'auto',
                        minHeight: '44px',
                        verticalAlign: 'middle',
                        overflow: 'visible'
                      }}
                      required
                    />
                    {showCustomerDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto" dir="rtl">
                        {ledgerParties
                          .filter(party => {
                            const searchLower = customerSearchTerm.toLowerCase();
                            const nameLower = (party.name || '').toLowerCase();
                            const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                            return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
                          })
                          .map(party => (
                            <div
                              key={party._id}
                              onClick={() => {
                                handleCustomerSelect(party._id);
                                setCustomerSearchTerm('');
                                setShowCustomerDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-700 text-right border-b border-gray-100 last:border-b-0"
                            >
                              {party.nameUrdu || party.name}
                            </div>
                          ))}
                        {ledgerParties.filter(party => {
                          const searchLower = customerSearchTerm.toLowerCase();
                          const nameLower = (party.name || '').toLowerCase();
                          const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                          return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
                        }).length === 0 && customerSearchTerm && (
                            <div className="px-3 py-2 text-gray-500 text-center">
                              کوئی گاہک نہیں ملا
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">گاہک کا فون نمبر:</label>
                  <input
                    type="text"
                    value={formData.sellerNumber}
                    onChange={(e) => handleInputChange('sellerNumber', e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'sellerNumber')}
                    ref={(el) => fieldRefs.current['sellerNumber'] = el}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700"
                    placeholder="فون نمبر"
                  />
                </div>
              </div>

              {/* Left Side - Supplier Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 mb-3">فروخت کیا گیا:</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام:</label>
                  <div className="relative" ref={supplierDropdownRef}>
                    <input
                      type="text"
                      value={formData.supplierNameUrdu || formData.supplierName ? (formData.supplierNameUrdu || formData.supplierName) : supplierSearchTerm}
                      onChange={(e) => {
                        setSupplierSearchTerm(e.target.value);
                        setShowSupplierDropdown(true);
                        if (!e.target.value) {
                          handleSupplierSelect('');
                        }
                      }}
                      onClick={handleSupplierSearchInputClick}
                      onFocus={() => setShowSupplierDropdown(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !showSupplierDropdown) {
                          handleKeyDown(e, 'supplierName');
                        }
                      }}
                      ref={(el) => {
                        supplierSearchInputRef.current = el;
                        fieldRefs.current['supplierName'] = el;
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700"
                      placeholder="سپلائر تلاش کریں..."
                      style={{
                        fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                        lineHeight: '1.8',
                        paddingTop: '12px',
                        paddingBottom: '12px',
                        fontSize: '13px',
                        height: 'auto',
                        minHeight: '44px',
                        verticalAlign: 'middle',
                        overflow: 'visible'
                      }}
                      required
                    />
                    {showSupplierDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto" dir="rtl">
                        {supplierParties
                          .filter(party => {
                            const searchLower = supplierSearchTerm.toLowerCase();
                            const nameLower = (party.name || '').toLowerCase();
                            const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                            return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
                          })
                          .map(party => (
                            <div
                              key={party._id}
                              onClick={() => {
                                handleSupplierSelect(party._id);
                                setSupplierSearchTerm('');
                                setShowSupplierDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-700 text-right border-b border-gray-100 last:border-b-0"
                            >
                              {party.nameUrdu || party.name}
                            </div>
                          ))}
                        {supplierParties.filter(party => {
                          const searchLower = supplierSearchTerm.toLowerCase();
                          const nameLower = (party.name || '').toLowerCase();
                          const nameUrduLower = (party.nameUrdu || '').toLowerCase();
                          return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
                        }).length === 0 && supplierSearchTerm && (
                            <div className="px-3 py-2 text-gray-500 text-center">
                              کوئی سپلائر نہیں ملا
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">گاڑی نمبر:</label>
                  <select
                    value={formData.supplierVehicleNumber}
                    onChange={(e) => handleVehicleNumberChange(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'supplierVehicleNumber')}
                    ref={(el) => fieldRefs.current['supplierVehicleNumber'] = el}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-gray-700"
                  >
                    <option value="">گاڑی نمبر منتخب کریں</option>
                    {vehicleNumbers.map((vehicleNum, index) => (
                      <option key={index} value={vehicleNum}>
                        {vehicleNum}
                      </option>
                    ))}
                  </select>
                  {vehicleNumbers.length === 0 && (
                    <input
                      type="text"
                      value={formData.supplierVehicleNumber}
                      onChange={(e) => handleVehicleNumberChange(e.target.value)}
                      onBlur={(e) => {
                        // Trigger lookup when user finishes typing
                        if (e.target.value && e.target.value.trim() !== '') {
                          handleVehicleNumberChange(e.target.value);
                        }
                      }}
                      onKeyDown={(e) => handleKeyDown(e, 'supplierVehicleNumber')}
                      ref={(el) => fieldRefs.current['supplierVehicleNumber'] = el}
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded text-gray-700"
                      placeholder="گاڑی نمبر درج کریں"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">خریداری کی تفصیل</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 border border-gray-300 text-right text-sm font-medium text-gray-700">تفصیل</th>
                      <th className="px-4 py-2 border border-gray-300 text-center text-sm font-medium text-gray-700">یونٹ</th>
                      <th className="px-4 py-2 border border-gray-300 text-center text-sm font-medium text-gray-700">دانہ</th>
                      <th className="px-4 py-2 border border-gray-300 text-center text-sm font-medium text-gray-700">مقدار</th>
                      <th className="px-4 py-2 border border-gray-300 text-center text-sm font-medium text-gray-700">فی یونٹ قیمت</th>
                      <th className="px-4 py-2 border border-gray-300 text-center text-sm font-medium text-gray-700">کل رقم</th>
                      <th className="px-4 py-2 border border-gray-300 text-center text-sm font-medium text-gray-700">عمل</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={`item-${index}-${item.description || item.descriptionUrdu || index}`}>
                        <td className="px-4 py-2 border border-gray-300 relative">
                          <div className="relative">
                            <input
                              type="text"
                              value={item.descriptionUrdu || item.description}
                              onChange={(e) => {
                                handleItemChange(index, 'descriptionUrdu', e.target.value);
                                if (!item.description) {
                                  handleItemChange(index, 'description', e.target.value);
                                }
                              }}
                              onFocus={() => {
                                // Show all available items when focused
                                if (availableItemNames.length > 0) {
                                  setItemSuggestions(prev => ({ ...prev, [index]: availableItemNames }));
                                }
                              }}
                              onBlur={() => handleBlur('descriptionUrdu', index)}
                              onKeyDown={(e) => handleKeyDown(e, 'description', index)}
                              ref={(el) => fieldRefs.current[`item_${index}_description`] = el}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-gray-700"
                              placeholder="آئٹم کا نام"
                            />
                            {itemSuggestions[index] && itemSuggestions[index].length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-40 overflow-y-auto">
                                {itemSuggestions[index].map((suggestion, sugIndex) => (
                                  <div
                                    key={sugIndex}
                                    onMouseDown={(e) => {
                                      e.preventDefault(); // Prevent blur before click
                                      handleItemNameSelect(index, suggestion);
                                    }}
                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-700"
                                  >
                                    {suggestion}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 border border-gray-300">
                          <select
                            value={item.unit || ''}
                            onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, 'unit', index)}
                            ref={(el) => fieldRefs.current[`item_${index}_unit`] = el}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-gray-700 text-center"
                            dir="rtl"
                          >
                            <option value="">{companyLanguage === 'ur' ? 'یونٹ' : 'Unit'}</option>
                            <option value="pali">{companyLanguage === 'ur' ? 'پلی' : 'Pali'}</option>
                            <option value="paiti">{companyLanguage === 'ur' ? 'پیتی' : 'Paiti'}</option>
                            <option value="bags">{companyLanguage === 'ur' ? 'بوریاں' : 'Bags'}</option>
                          </select>
                        </td>
                        <td className="px-4 py-2 border border-gray-300">
                          <input
                            type="number"
                            step="0.01"
                            value={item.dana !== null && item.dana !== undefined ? (typeof item.dana === 'number' ? item.dana : parseFloat(item.dana) || '') : ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleItemChange(index, 'dana', newValue);
                            }}
                            onFocus={() => handleFocus('dana', index)}
                            onBlur={() => {
                              handleBlur('dana', index);
                              // Ensure numeric value is stored
                              const currentValue = formData.items[index]?.dana;
                              if (currentValue !== '' && currentValue !== null && currentValue !== undefined) {
                                const numValue = parseFloat(currentValue);
                                if (!isNaN(numValue)) {
                                  handleItemChange(index, 'dana', numValue);
                                }
                              }
                            }}
                            onKeyDown={(e) => handleKeyDown(e, 'dana', index)}
                            ref={(el) => fieldRefs.current[`item_${index}_dana`] = el}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-gray-700 text-center"
                            placeholder="دانہ"
                            dir="rtl"
                            required
                          />
                        </td>
                        <td className="px-4 py-2 border border-gray-300">
                          <input
                            type="number"
                            step="0.01"
                            value={getDisplayValue('quantity', index)}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            onFocus={() => handleFocus('quantity', index)}
                            onBlur={() => handleBlur('quantity', index)}
                            onKeyDown={(e) => handleKeyDown(e, 'quantity', index)}
                            ref={(el) => fieldRefs.current[`item_${index}_quantity`] = el}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-gray-700 text-center"
                            placeholder="0"
                            required
                            max={(() => {
                              const itemName = item.descriptionUrdu || item.description || '';
                              const remaining = getRemainingQtyForItem(itemName, index);
                              return remaining === null ? undefined : remaining;
                            })()}
                          />
                          {(() => {
                            const itemName = item.descriptionUrdu || item.description || '';
                            const remaining = getRemainingQtyForItem(itemName, index);
                            if (remaining === null) return null;
                            return (
                              <div className="mt-1 text-[11px] text-gray-600 text-center">
                                باقی: <span className="font-semibold">{remaining.toFixed(2)}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-2 border border-gray-300">
                          <input
                            type="number"
                            step="0.01"
                            value={getDisplayValue('ratePerUnit', index)}
                            onChange={(e) => handleItemChange(index, 'ratePerUnit', e.target.value)}
                            onFocus={() => handleFocus('ratePerUnit', index)}
                            onBlur={() => handleBlur('ratePerUnit', index)}
                            onKeyDown={(e) => handleKeyDown(e, 'ratePerUnit', index)}
                            ref={(el) => fieldRefs.current[`item_${index}_ratePerUnit`] = el}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-gray-700 text-center"
                            placeholder="0"
                            required
                          />
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center">
                          <span className="text-gray-700 font-semibold">
                            {(item.lineTotal || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-2 border border-gray-300 text-center">
                          {formData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                            >
                              حذف
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="mt-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                >
                  + آئٹم شامل کریں
                </button>
              </div>
            </div>

            {/* Summary Section */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div></div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">ذیلی کل:</label>
                  <span className="text-gray-900 font-semibold">{(formData.subtotal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">رعایت:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={getDisplayValue('discount')}
                    onChange={(e) => handleInputChange('discount', e.target.value)}
                    onFocus={() => handleFocus('discount')}
                    onBlur={() => handleBlur('discount')}
                    onKeyDown={(e) => handleKeyDown(e, 'discount')}
                    ref={(el) => fieldRefs.current['discount'] = el}
                    className="w-32 px-2 py-1 border border-gray-300 rounded text-gray-700 text-right"
                    placeholder="0"
                  />
                </div>
                <div className="flex justify-between items-center border-t-2 border-gray-400 pt-2">
                  <label className="text-lg font-bold text-gray-900">کل رقم:</label>
                  <span className="text-lg font-bold text-gray-900">{(formData.totalAmount || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">ادائیگی کا طریقہ:</label>
                  <select
                    value={formData.paymentMethodId}
                    onChange={(e) => {
                      const id = e.target.value;
                      handleInputChange('paymentMethodId', id);
                      const m = paymentMethods.find(pm => (pm._id?.toString() || pm._id) === (id?.toString() || id));
                      handleInputChange('paymentMethod', m ? (m.nameUrdu || m.name) : '');
                    }}
                    onKeyDown={(e) => handleKeyDown(e, 'paymentMethod')}
                    ref={(el) => fieldRefs.current['paymentMethod'] = el}
                    className="w-32 px-2 py-1 border border-gray-300 rounded text-gray-700"
                  >
                    <option value="">منتخب کریں</option>
                    {paymentMethods.length > 0 && (
                      paymentMethods.map((method) => (
                        <option key={method._id} value={method._id}>
                          {method.nameUrdu || method.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-gray-700">ادا کی گئی رقم:</label>
                  <input
                    type="number"
                    step="0.01"
                    value={getDisplayValue('amountPaid')}
                    onChange={(e) => handleInputChange('amountPaid', e.target.value)}
                    onFocus={() => handleFocus('amountPaid')}
                    onBlur={() => handleBlur('amountPaid')}
                    onKeyDown={(e) => handleKeyDown(e, 'amountPaid')}
                    ref={(el) => fieldRefs.current['amountPaid'] = el}
                    className="w-32 px-2 py-1 border border-gray-300 rounded text-gray-700 text-right"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => navigate('/company/dashboard')}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                منسوخ
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"
              >
                {isSaving
                  ? (editSaleId ? 'اپ ڈیٹ ہو رہا ہے...' : 'بنا رہے ہیں...')
                  : (editSaleId ? 'سیل اپ ڈیٹ کریں' : 'سیل بنائیں')
                }
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Sale List Section - Columnar View with Borders */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="bg-white shadow-lg rounded-lg p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h2 className={`text-xl font-bold ${language === 'ur' ? 'font-urdu' : ''}`}>
              {language === 'ur' ? 'سیلز کی فہرست' : 'Sales List'}
              <span className={`text-sm font-normal text-gray-600 ${language === 'ur' ? 'mr-2' : 'ml-2'}`}>
                ({allSales.length})
              </span>
            </h2>

            <div className="flex flex-1 max-w-md w-full gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={filterSearchTerm}
                  onChange={(e) => setFilterSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all text-right"
                  placeholder={language === 'ur' ? 'تلاش کریں (نام یا سیل نمبر)...' : 'Search (Name or Sale #)...'}
                  dir="rtl"
                />
                <button
                  onClick={() => setShowFilterSearchUrduKeyboard(!showFilterSearchUrduKeyboard)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-emerald-600 transition-colors"
                  title="اردو کی بورڈ"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                </button>
              </div>
            </div>

            {selectedSales.length > 0 && (
              <button
                onClick={async () => {
                  if (!window.confirm(language === 'ur' ? `کیا آپ واقعی ${selectedSales.length} سیلز ختم کرنا چاہتے ہیں؟` : `Are you sure you want to delete ${selectedSales.length} sales?`)) return;
                  try {
                    const token = localStorage.getItem('companyToken');
                    await Promise.all(selectedSales.map(id => api.delete(`/sale/company/${id}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    })));
                    toast.success(language === 'ur' ? 'سیلز کامیابی سے ختم کر دی گئیں' : 'Sales deleted successfully');
                    setSelectedSales([]);
                    fetchCompanyData();
                  } catch (error) {
                    toast.error(language === 'ur' ? 'ختم کرنے میں ناکام' : 'Failed to delete');
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
              >
                {language === 'ur' ? `منتخب شدہ حذف کریں (${selectedSales.length})` : `Delete Selected (${selectedSales.length})`}
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse border border-gray-300">
              <thead className="bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-600 text-white">
                <tr>
                  <th className={`px-4 py-1.5 text-center font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    <input
                      type="checkbox"
                      checked={allSales.length > 0 && selectedSales.length === allSales.length}
                      onChange={() => {
                        if (selectedSales.length === allSales.length) setSelectedSales([]);
                        else setSelectedSales(allSales.map(s => s._id));
                      }}
                      className="w-4 h-4 cursor-pointer"
                    />
                  </th>
                  <th className={`px-4 py-1.5 text-right font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'تاریخ' : 'Date'}
                  </th>
                  <th className={`px-4 py-1.5 text-right font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'سیل نمبر' : 'Sale Number'}
                  </th>
                  <th className={`px-4 py-1.5 text-right font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'سپلائر' : 'Supplier'}
                  </th>
                  <th className={`px-4 py-1.5 text-right font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'کسٹمر' : 'Customer'}
                  </th>
                  <th className={`px-4 py-1.5 text-right font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'کل رقم' : 'Amount'}
                  </th>
                  <th className={`px-4 py-1.5 text-right font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'باقی' : 'Remaining'}
                  </th>
                  <th className={`px-4 py-1.5 text-center font-bold border border-emerald-700 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'عمل' : 'Action'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {allSales
                  .filter(sale => {
                    const st = filterSearchTerm.toLowerCase();
                    return (
                      (sale.serialNumber?.toString() || '').toLowerCase().includes(st) ||
                      (sale.customerName?.toString() || '').toLowerCase().includes(st) ||
                      (sale.customerNameUrdu?.toString() || '').toLowerCase().includes(st) ||
                      (sale.supplierName?.toString() || '').toLowerCase().includes(st) ||
                      (sale.supplierNameUrdu?.toString() || '').toLowerCase().includes(st) ||
                      (sale.sellerName?.toString() || '').toLowerCase().includes(st) ||
                      (sale.sellerNameUrdu?.toString() || '').toLowerCase().includes(st)
                    );
                  })
                  .map((sale, index) => (
                    <tr
                      key={sale._id}
                      className={`border-b border-gray-200 hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedSales.includes(sale._id) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-1.5 text-center border border-gray-300">
                        <input
                          type="checkbox"
                          checked={selectedSales.includes(sale._id)}
                          onChange={() => {
                            if (selectedSales.includes(sale._id)) setSelectedSales(prev => prev.filter(id => id !== sale._id));
                            else setSelectedSales(prev => [...prev, sale._id]);
                          }}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                      <td className={`px-4 py-1.5 text-right border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {new Date(sale.date).toLocaleDateString(language === 'ur' ? 'ur-PK' : 'en-GB')}
                      </td>
                      <td className={`px-4 py-1.5 text-right border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {sale.serialNumber || '-'}
                      </td>
                      <td className={`px-4 py-1.5 text-right border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {sale.supplierNameUrdu || sale.supplierName || '-'}
                      </td>
                      <td className={`px-4 py-1.5 text-right border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                        {sale.sellerNameUrdu || sale.sellerName || '-'}
                      </td>
                      <td className="px-4 py-1.5 text-right font-semibold text-green-700 border border-gray-300">
                        {(parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0).toLocaleString()}
                      </td>
                      <td className={`px-4 py-1.5 text-right font-semibold border border-gray-300 ${((parseFloat(sale.totalAmount) || 0) - (parseFloat(sale.amountPaid) || 0)) > 0 ? 'text-red-600' : 'text-green-700'}`}>
                        {((parseFloat(sale.totalAmount) || 0) - (parseFloat(sale.amountPaid) || 0)).toLocaleString()}
                      </td>
                      <td className="px-4 py-1.5 text-center border border-gray-300">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/sale/view/${sale._id}?lang=${language === 'ur' ? 'ur' : 'en'}`)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition-all"
                          >
                            {language === 'ur' ? 'دیکھیں' : 'View'}
                          </button>
                          <button
                            onClick={() => {
                              navigate(`/sale/create?edit=${sale._id}`);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="bg-brand-button hover:bg-yellow-400 text-gray-900 px-3 py-1 rounded text-sm transition-all"
                          >
                            {language === 'ur' ? 'تبدیل کریں' : 'Edit'}
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm(language === 'ur' ? 'کیا آپ واقعی یہ سیل ختم کرنا چاہتے ہیں؟' : 'Are you sure you want to delete this sale?')) return;
                              try {
                                const token = localStorage.getItem('companyToken');
                                await api.delete(`/sale/company/${sale._id}`, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success(language === 'ur' ? 'سیل کامیابی سے ختم ہو گئی' : 'Sale deleted successfully');
                                fetchCompanyData();
                              } catch (error) {
                                toast.error(language === 'ur' ? 'ختم کرنے میں ناکام' : 'Failed to delete');
                              }
                            }}
                            className="bg-brand-alert hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition-all"
                          >
                            {language === 'ur' ? 'ختم کریں' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot className="bg-gray-100">
                <tr>
                  <td colSpan={5} className={`px-4 py-3 text-right font-bold border border-gray-300 ${language === 'ur' ? 'font-urdu' : ''}`}>
                    {language === 'ur' ? 'کل' : 'Total'}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-green-700 border border-gray-300">
                    {allSales
                      .filter(sale => {
                        const st = filterSearchTerm.toLowerCase();
                        return (
                          (sale.serialNumber?.toString() || '').toLowerCase().includes(st) ||
                          (sale.customerName?.toString() || '').toLowerCase().includes(st) ||
                          (sale.customerNameUrdu?.toString() || '').toLowerCase().includes(st) ||
                          (sale.supplierName?.toString() || '').toLowerCase().includes(st) ||
                          (sale.supplierNameUrdu?.toString() || '').toLowerCase().includes(st)
                        );
                      })
                      .reduce((sum, s) => sum + (parseFloat(s.totalAmount) || parseFloat(s.total) || 0), 0).toLocaleString()}
                  </td>
                  <td className="border border-gray-300" colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Urdu Keyboard for Filter Search */}
      <UrduKeyboard
        isVisible={showFilterSearchUrduKeyboard}
        onKeyPress={(char) => setFilterSearchTerm(prev => prev + char)}
        onClose={() => setShowFilterSearchUrduKeyboard(false)}
      />

      {/* Urdu Keyboard for Customer Search */}
      <UrduKeyboard
        isVisible={showCustomerUrduKeyboard}
        onKeyPress={handleCustomerSearchUrduKeyPress}
        onClose={() => setShowCustomerUrduKeyboard(false)}
      />

      {/* Urdu Keyboard for Supplier Search */}
      <UrduKeyboard
        isVisible={showSupplierUrduKeyboard}
        onKeyPress={handleSupplierSearchUrduKeyPress}
        onClose={() => setShowSupplierUrduKeyboard(false)}
      />
    </>
  );
}

export default CreateSale;

